import React, { useState } from 'react';
import { BACKEND_URL } from '../config/api';
import { cn } from '../lib/utils';

/**
 * Resolve a backend-relative avatar URL to an absolute URL.
 *
 * Backend stores avatars as relative paths (e.g. "/api/uploads/avatars/file.png")
 * because the same DB row is consumed by web + future mobile clients. The web
 * client must prefix REACT_APP_BACKEND_URL / VITE_BACKEND_URL to make the
 * <img src> resolve, or the browser treats it as relative to the frontend
 * origin and 404s (frontend dev server doesn't have those files).
 *
 *   null/undefined/""  → null
 *   "http(s)://..."    → returned as-is (absolute)
 *   "data:..."/"blob:" → returned as-is
 *   "/api/uploads/..." → BACKEND_URL + path
 *   "uploads/..."      → returned as-is (treat as already-resolved relative)
 */
export const resolveAvatarUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

const getInitial = (name, email) => {
  const s = (name || email || '').trim();
  return s ? s[0].toUpperCase() : '?';
};

/**
 * Unified avatar renderer.
 *
 * - Prefixes relative `src` values with BACKEND_URL via resolveAvatarUrl.
 * - Falls back to an initial letter (or a custom `fallback` node) when the
 *   src is missing OR when the <img> errors at runtime (e.g. file was
 *   deleted from disk but the DB row still references it).
 *
 * Drop-in usage:
 *   <UserAvatar src={user.avatar_url} name={user.name} size="h-10 w-10" />
 *
 * Custom fallback (e.g. icon instead of initial):
 *   <UserAvatar src={u.avatar_url} name={u.name} fallback={<User className="h-8 w-8" />} />
 */
const UserAvatar = ({
  src,
  name,
  email,
  alt,
  size = 'h-10 w-10',
  className,
  imgClassName,
  fallback,
  'data-testid': dataTestId,
}) => {
  const resolved = resolveAvatarUrl(src);
  const [errored, setErrored] = useState(false);
  const showImg = !!resolved && !errored;

  return (
    <div
      data-testid={dataTestId}
      className={cn(
        size,
        'rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary overflow-hidden flex-shrink-0',
        className
      )}
    >
      {showImg ? (
        <img
          src={resolved}
          alt={alt || name || ''}
          onError={() => setErrored(true)}
          className={cn('w-full h-full object-cover', imgClassName)}
        />
      ) : (
        fallback ?? getInitial(name, email)
      )}
    </div>
  );
};

export default UserAvatar;
